import {
  MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ScheduleIcon from '@mui/icons-material/Schedule'
import ChatBubbleIcon from '@mui/icons-material/ChatBubble'
import {
  DayHeader,
  joinPeriods,
  parsePeriods,
  PeriodNum,
  pct,
  TOTAL_HOURS,
  WeekTrack,
  Window,
} from './RulesWorkbench.timeline'
import { FONT_SANS, TOKENS } from './SchedulerDesign'
import { SchedulerComment } from './Scheduler.data'
import {
  downtimeAnchor,
  formatWeekHour,
  partitionDowntimeComments,
} from './ScheduleComments.anchor'
import {
  COMMENT_ACCENT,
  CommentThread,
} from './ScheduleCommentPopover.view'

/** Divider row naming what the thread under it is attached to — the resource
 *  as a whole, or one window's hours. */
/** One comment section — heading, its comments and the box that posts to it,
 *  enclosed so the grouping is unambiguous. See the note at the call site. */
const SECTION_SX = {
  border: `1px solid ${TOKENS.line200}`,
  borderRadius: '4px',
  overflow: 'hidden',
  mb: '10px',
} as const

const DownTimeCommentSubHeader = ({
  label,
  hint,
}: {
  label: string
  hint?: string
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      p: '6px 12px',
      bgcolor: TOKENS.surface0,
      borderTop: `1px solid ${TOKENS.line200}`,
      borderBottom: `1px solid ${TOKENS.line100}`,
    }}
  >
    <Box
      sx={{
        fontSize: 10.5,
        fontWeight: 700,
        color: TOKENS.ink700,
        fontFamily: FONT_SANS,
      }}
    >
      {label}
    </Box>
    {hint && (
      <Box
        sx={{ fontSize: 10, color: TOKENS.ink400, fontFamily: FONT_SANS }}
      >
        {hint}
      </Box>
    )}
  </Box>
)

// ─── read-only preview shown in the grid cell ────────────────────────────────

export const DownTimeMiniTimeline = ({
  value,
}: {
  value: string | null | undefined
}) => {
  const windows = parsePeriods(value)
  if (!windows.length) {
    return (
      <Box sx={{ color: TOKENS.ink300, fontSize: 12, fontFamily: FONT_SANS }}>
        —
      </Box>
    )
  }
  return (
    <Box sx={{ width: '100%' }} title={String(value ?? '')}>
      <WeekTrack height={14} radius={2}>
        {windows.map((w, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              left: `${pct(w[0])}%`,
              width: `${pct(Math.max(0, w[1] - w[0]))}%`,
              top: 0,
              bottom: 0,
              background: TOKENS.ganttDowntime,
            }}
          />
        ))}
      </WeekTrack>
    </Box>
  )
}

// ─── interactive band on the editor's timeline: hover to reveal a remove ×────

const EditableBand = ({
  start,
  end,
  onRemove,
}: {
  start: number
  end: number
  onRemove: () => void
}) => {
  const [hover, setHover] = useState(false)
  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onMouseDown={(e: ReactMouseEvent) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        left: `${pct(start)}%`,
        width: `${pct(Math.max(0, end - start))}%`,
        top: 2,
        bottom: 2,
        minWidth: 4,
        background: TOKENS.ganttDowntime,
        borderRadius: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
    >
      {hover && (
        <Box
          component="span"
          onMouseDown={(e: ReactMouseEvent) => {
            e.stopPropagation()
            onRemove()
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 15,
            height: 15,
            mr: '2px',
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.4)',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <CloseIcon sx={{ fontSize: 11, color: '#fff' }} />
        </Box>
      )}
    </Box>
  )
}

// ─── full editor: drag-to-create timeline + numeric chip list ───────────────

const DownTimeEditor = ({
  windows,
  onChange,
  maxWindows,
}: {
  windows: Window[]
  onChange: (next: Window[]) => void
  /** Cap on how many windows this value may hold. A **trial** is a single
   *  hard-fixed window (the engine rejects a multi-window trial outright), so
   *  the trials table passes 1: dragging then replaces the window rather than
   *  adding a second, and "Add window" disappears once one exists. Undefined
   *  keeps the unlimited downtime behaviour. */
  maxWindows?: number
}) => {
  // At the cap, a new drag replaces rather than appends — the user asked for a
  // window *there*, and refusing the drag silently would read as a broken UI.
  const addWindow = (w: Window) =>
    onChange(
      maxWindows != null && windows.length >= maxWindows
        ? [...windows.slice(0, Math.max(0, maxWindows - 1)), w]
        : [...windows, w]
    )
  const atCap = maxWindows != null && windows.length >= maxWindows
  const trackRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<{ start: number; end: number } | null>(null)

  const hourAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect || rect.width === 0) return 0
    const frac = (clientX - rect.left) / rect.width
    return Math.min(TOTAL_HOURS, Math.max(0, Math.round(frac * TOTAL_HOURS)))
  }

  const onTrackMouseDown = (e: ReactMouseEvent) => {
    const startHour = hourAt(e.clientX)
    setDrag({ start: startHour, end: startHour })

    const onMove = (ev: globalThis.MouseEvent) => {
      setDrag((d) => (d ? { start: d.start, end: hourAt(ev.clientX) } : d))
    }
    const onUp = (ev: globalThis.MouseEvent) => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      setDrag((d) => {
        if (d) {
          const endHour = hourAt(ev.clientX)
          const s = Math.min(d.start, endHour)
          const en = Math.max(d.start, endHour)
          if (en - s >= 1) addWindow([s, en])
        }
        return null
      })
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const dragBand =
    drag && Math.abs(drag.end - drag.start) >= 1
      ? [Math.min(drag.start, drag.end), Math.max(drag.start, drag.end)]
      : null

  return (
    <Box>
      <DayHeader />
      <Box
        ref={trackRef}
        onMouseDown={onTrackMouseDown}
        sx={{ cursor: 'crosshair' }}
      >
        <WeekTrack height={28}>
          {windows.map((w, i) => (
            <EditableBand
              key={i}
              start={w[0]}
              end={w[1]}
              onRemove={() => onChange(windows.filter((_, j) => j !== i))}
            />
          ))}
          {dragBand && (
            <Box
              sx={{
                position: 'absolute',
                left: `${pct(dragBand[0])}%`,
                width: `${pct(dragBand[1] - dragBand[0])}%`,
                top: 2,
                bottom: 2,
                background: TOKENS.brandTeal,
                opacity: 0.5,
                borderRadius: '2px',
                pointerEvents: 'none',
              }}
            />
          )}
        </WeekTrack>
      </Box>
      <Box
        sx={{
          fontSize: 11,
          color: TOKENS.ink400,
          fontFamily: FONT_SANS,
          mt: '6px',
        }}
      >
        Drag on the timeline to add a window, or fine-tune the hours below.
      </Box>

      <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: 1.25 }}>
        {windows.map((w, i) => (
          <Box
            key={i}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              p: '6px 9px',
              border: `1px solid ${TOKENS.line200}`,
              borderRadius: '3px',
              background: TOKENS.surface50,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 13, color: TOKENS.ganttDowntime }} />
            <PeriodNum
              value={w[0]}
              onChange={(v) =>
                onChange(windows.map((q, j) => (j === i ? [v, q[1]] : q)))
              }
            />
            <Box component="span" sx={{ fontSize: 11, color: TOKENS.ink300 }}>
              →
            </Box>
            <PeriodNum
              value={w[1]}
              onChange={(v) =>
                onChange(windows.map((q, j) => (j === i ? [q[0], v] : q)))
              }
            />
            <CloseIcon
              sx={{ fontSize: 13, color: TOKENS.ink300, cursor: 'pointer' }}
              onClick={() => onChange(windows.filter((_, j) => j !== i))}
            />
          </Box>
        ))}
        <Box
          component="button"
          onClick={() => {
            const lastEnd = windows.length ? windows[windows.length - 1][1] : 0
            const start = Math.min(TOTAL_HOURS - 1, lastEnd)
            addWindow([start, Math.min(TOTAL_HOURS, start + 8)])
          }}
          sx={{
            display: atCap ? 'none' : 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            height: 30,
            px: '11px',
            borderRadius: '3px',
            border: `1px dashed ${TOKENS.line300}`,
            background: 'transparent',
            color: TOKENS.brandTeal,
            cursor: 'pointer',
            fontSize: 11.5,
            fontWeight: 600,
            fontFamily: FONT_SANS,
            '& svg': { fontSize: 14 },
          }}
        >
          <AddIcon />
          Add window
        </Box>
      </Box>
    </Box>
  )
}

// ─── dialog wrapper ───────────────────────────────────────────────────────────

/**
 * What the dialog needs to show a comment thread against this resource's
 * downtime. Omitted by the Trials table, which reuses this editor for a
 * different thing and has no thread of its own.
 */
/** The two clean questions a trial window asks, and what they currently say.
 *
 *  One pair per popup rather than one for the trial, because the line and the
 *  kitchen are cleaned by different rigs on their own windows — so a planner
 *  can prepare the line while leaving the kitchen alone. The engine reserves
 *  each of the four slots independently (`trial_prep::plan_trial_cleans`). */
export type DownTimeCleanToggles = {
  /** Which unit this popup is for — only used to word the labels. */
  unit: 'line' | 'kitchen'
  before: boolean
  after: boolean
}

export type DownTimeCommentContext = {
  schedulerId: string
  iteration: string
  weekNumber: number
  /** Resource code the windows belong to — the whole of the comment anchor. */
  resourceName: string
  /** Carried onto new comments for display only; it takes no part in
   *  matching, because the column is editable free text. */
  resourceType: string
}

export const DownTimeWindowsDialog = ({
  open,
  resourceLabel,
  initialValue,
  onClose,
  onSave,
  title = 'Downtime windows',
  maxWindows,
  comments,
  commentContext,
  currentUserEmail = '',
  cleanToggles,
}: {
  open: boolean
  resourceLabel: string
  initialValue: string | null | undefined
  onClose: () => void
  /** `cleans` is present only when `cleanToggles` was — the dialog hands back
   *  whatever the planner left the switches on, so the caller writes the
   *  window and the flags as one row edit. */
  onSave: (value: string, cleans?: { before: boolean; after: boolean }) => void
  /** Heading before the resource name. Trials say "Trial window" — same
   *  editor, different thing being entered. */
  title?: string
  /** See `DownTimeEditor`'s prop of the same name. */
  maxWindows?: number
  /** Existing comments on this resource's downtime for the week. */
  comments?: SchedulerComment[]
  /** Present enables the comments section; absent hides it entirely. */
  commentContext?: DownTimeCommentContext
  currentUserEmail?: string
  /** Present enables the two clean toggles; absent hides them entirely.
   *
   *  Only a TRIAL window has them. A downtime window is an absence of work —
   *  there is nothing to clean before or after it — so the downtime tab
   *  passes nothing and the dialog looks exactly as it did. */
  cleanToggles?: DownTimeCleanToggles
}) => {
  const [windows, setWindows] = useState<Window[]>(() =>
    parsePeriods(initialValue)
  )

  // Discard any in-progress edits and re-seed from the committed value each
  // time the dialog opens (so a Cancel doesn't leak into the next open).
  useEffect(() => {
    if (open) setWindows(parsePeriods(initialValue))
  }, [open, initialValue])

  // The clean switches, re-seeded on open for the same reason the windows
  // are: Cancel must not leak a flipped switch into the next trial opened.
  const [cleanBefore, setCleanBefore] = useState(cleanToggles?.before ?? true)
  const [cleanAfter, setCleanAfter] = useState(cleanToggles?.after ?? true)
  useEffect(() => {
    if (open) {
      setCleanBefore(cleanToggles?.before ?? true)
      setCleanAfter(cleanToggles?.after ?? true)
    }
  }, [open, cleanToggles?.before, cleanToggles?.after])

  // Save posts any comment still sitting in a composer, then saves the
  // windows. A comment is committed by Ctrl+Enter or the Comment button and by
  // nothing else, so a planner who typed one and pressed Save lost it with no
  // error and no row — which reads as "downtime comments don't save" rather
  // than "that text was never posted".
  //
  // Two steps rather than one because posting happens in the threads: raising
  // `flushSignal` lets each one submit on its own effect, and child effects
  // run before this component's, so deferring the window save by one render is
  // enough for both to happen on a single click. Cancel deliberately does NOT
  // flush — discarding is what it means.
  const [flushSignal, setFlushSignal] = useState(0)
  const [pendingSave, setPendingSave] = useState<string | null>(null)
  useEffect(() => {
    if (pendingSave === null) return
    setPendingSave(null)
    onSave(
      pendingSave,
      cleanToggles ? { before: cleanBefore, after: cleanAfter } : undefined
    )
  }, [pendingSave, onSave, cleanToggles, cleanBefore, cleanAfter])

  // Which comment belongs to which window, recomputed as the planner edits so
  // a thread follows its window rather than the hours it was written at.
  const partitioned = useMemo(
    () => partitionDowntimeComments(comments ?? [], windows),
    [comments, windows]
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        {title} — {resourceLabel}
      </DialogTitle>
      <DialogContent>
        <DownTimeEditor
          windows={windows}
          onChange={setWindows}
          maxWindows={maxWindows}
        />

        {/* Trial cleans. Shown only when the caller supplied the toggles, so
            the downtime tab is unchanged — a downtime window is an absence of
            work and has nothing to clean around.

            Off is a real instruction, not an omission: the engine reserves no
            rig slot on that side, and the window then blocks its hours and
            nothing else, exactly like downtime. Both default ON, which is what
            every trial did before the switches existed. */}
        {cleanToggles && (
          <Box
            sx={{
              mt: '14px',
              border: `1px solid ${TOKENS.line200}`,
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                p: '6px 12px',
                bgcolor: TOKENS.surface0,
                borderBottom: `1px solid ${TOKENS.line100}`,
                fontSize: 10.5,
                fontWeight: 700,
                color: TOKENS.ink700,
                fontFamily: FONT_SANS,
              }}
            >
              Cleaning — this {cleanToggles.unit}
            </Box>
            {(
              [
                ['before', cleanBefore, setCleanBefore] as const,
                ['after', cleanAfter, setCleanAfter] as const,
              ] as const
            ).map(([side, value, set]) => (
              <Box
                key={side}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  p: '6px 8px 6px 12px',
                }}
              >
                <Box>
                  <Box
                    sx={{
                      fontSize: 12,
                      color: TOKENS.ink900,
                      fontFamily: FONT_SANS,
                    }}
                  >
                    Clean {side} the trial
                  </Box>
                  <Box
                    sx={{
                      fontSize: 10,
                      color: TOKENS.ink400,
                      fontFamily: FONT_SANS,
                    }}
                  >
                    {value
                      ? side === 'before'
                        ? `The ${cleanToggles.unit} is cleaned so the trial starts clean.`
                        : `The ${cleanToggles.unit} is cleaned once the trial ends.`
                      : 'No clean — the window just blocks the hours, like downtime.'}
                  </Box>
                </Box>
                <Switch
                  checked={value}
                  onChange={(e) => set(e.target.checked)}
                  size="small"
                  inputProps={{
                    'aria-label': `Clean ${side} the trial on this ${cleanToggles.unit}`,
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Comments, resource-level first and then one thread per window.
            Storage stays per resource — a window has no id to key on — so the
            split is done here by OVERLAP against the window each comment
            recorded (see `partitionDowntimeComments`).

            `windows` is live editor state, not the committed value, which is
            what makes a thread follow a window the planner has just dragged.
            The trade is that Cancel reverts the window but not a comment
            written against its new hours; overlap matching means that comment
            almost always still lands on the reverted window, and falls back to
            resource-level rather than disappearing if it does not.

            Threads read and write immediately, so they are unaffected by
            Cancel below. */}
        {commentContext && (
          <Box
            sx={{
              mt: 2,
              border: `1px solid ${TOKENS.line200}`,
              borderRadius: '3px',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                p: '8px 12px',
                bgcolor: TOKENS.panelHead,
                borderBottom: `1px solid ${TOKENS.line200}`,
              }}
            >
              <ChatBubbleIcon
                sx={{ fontSize: 12, color: COMMENT_ACCENT }}
              />
              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: TOKENS.ink500,
                  fontFamily: FONT_SANS,
                }}
              >
                Comments
              </Box>
              <Box
                sx={{
                  fontSize: 10.5,
                  color: TOKENS.ink400,
                  fontFamily: FONT_SANS,
                }}
              >
                week {commentContext.weekNumber}
              </Box>
            </Box>

            {/* Each section is a closed block — heading, list and composer
                inside one border. The composer sits BELOW its own list, so
                without an enclosure it lands directly above the NEXT
                heading and reads as belonging to it: that is how 'window 1
                comment' was filed against the resource and 'window 2
                comment' against window 1. The border says where a box
                posts; the placeholder says it again in words. */}
            <Box sx={SECTION_SX}>
              <DownTimeCommentSubHeader
                label="This resource"
                hint="applies to the whole week"
              />
              <CommentThread
                schedulerId={commentContext.schedulerId}
                iteration={commentContext.iteration}
                weekNumber={commentContext.weekNumber}
                target={{
                  ...downtimeAnchor(
                    commentContext.resourceName,
                    commentContext.resourceType
                  ),
                  startTime: 0,
                  endTime: 0,
                  productDescription: commentContext.resourceName,
                }}
                comments={partitioned.resourceLevel}
                currentUserEmail={currentUserEmail}
                emptyText="No comments on this resource's downtime yet."
                maxHeight={180}
                flushSignal={flushSignal}
                composerPlaceholder="Comment on this resource — the whole week…"
                // The windows editor above is what the dialog is for —
                // stealing focus into the comment box on open would fight it.
                autoFocus={false}
              />
            </Box>

            {windows.map((w, i) => (
              <Box key={`${w[0]}-${w[1]}-${i}`} sx={SECTION_SX}>
                <DownTimeCommentSubHeader
                  label={`${formatWeekHour(w[0])} → ${formatWeekHour(w[1])}`}
                  hint={
                    partitioned.byWindow[i]?.length
                      ? `${partitioned.byWindow[i].length} comment${
                          partitioned.byWindow[i].length === 1 ? '' : 's'
                        }`
                      : undefined
                  }
                />
                <CommentThread
                  schedulerId={commentContext.schedulerId}
                  iteration={commentContext.iteration}
                  weekNumber={commentContext.weekNumber}
                  target={{
                    ...downtimeAnchor(
                      commentContext.resourceName,
                      commentContext.resourceType
                    ),
                    startTime: w[0],
                    endTime: w[1],
                    productDescription: commentContext.resourceName,
                  }}
                  comments={partitioned.byWindow[i] ?? []}
                  currentUserEmail={currentUserEmail}
                  emptyText="No comments on this window yet."
                  maxHeight={180}
                  flushSignal={flushSignal}
                  composerPlaceholder={`Comment on ${formatWeekHour(
                    w[0]
                  )} → ${formatWeekHour(w[1])}…`}
                  autoFocus={false}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            setFlushSignal((n) => n + 1)
            setPendingSave(joinPeriods(windows))
          }}
          variant="contained"
          size="small"
          sx={{ textTransform: 'none' }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
